import type { PaginationMeta } from '../models';

type RawPaginationMeta = Record<string, unknown>;

export function extractCustomerReportRawMeta(response: Record<string, unknown>): RawPaginationMeta {
  const nested = (response.meta as RawPaginationMeta | undefined) ?? {};
  return {
    ...nested,
    total: nested.total ?? response.total,
    current_page: nested.current_page ?? nested.currentPage ?? response.current_page ?? response.currentPage,
    last_page: nested.last_page ?? nested.lastPage ?? response.last_page ?? response.lastPage,
    per_page: nested.per_page ?? nested.perPage ?? response.per_page ?? response.perPage,
    from: nested.from ?? response.from,
    to: nested.to ?? response.to,
  };
}

export function normalizeCustomerPaginationMeta(
  raw: RawPaginationMeta | undefined,
  dataLength: number,
  requestedPage = 1,
  requestedPerPage = 25,
): PaginationMeta {
  const meta = raw ?? {};
  const total = Number(meta.total ?? 0);
  const per_page = Number(meta.per_page ?? meta.perPage ?? requestedPerPage) || requestedPerPage;
  const current_page = Number(meta.current_page ?? meta.currentPage ?? requestedPage) || requestedPage;
  const last_page = Number(
    meta.last_page ?? meta.lastPage ?? (total > 0 ? Math.max(1, Math.ceil(total / per_page)) : 1),
  );

  const rawFrom = meta.from;
  const rawTo = meta.to;
  const hasValidRange = rawFrom != null && rawTo != null && Number(rawFrom) > 0 && Number(rawTo) >= Number(rawFrom);

  let from: number;
  let to: number;

  if (hasValidRange) {
    from = Number(rawFrom);
    to = Number(rawTo);
  } else if (dataLength === 0 || total === 0) {
    from = 0;
    to = 0;
  } else {
    from = (current_page - 1) * per_page + 1;
    to = Math.min(from + dataLength - 1, total);
  }

  return {
    current_page,
    from,
    last_page,
    per_page,
    to,
    total,
    path: String(meta.path ?? ''),
    first_page_url: String(meta.first_page_url ?? ''),
    last_page_url: String(meta.last_page_url ?? ''),
    next_page_url: (meta.next_page_url as string | null) ?? null,
    prev_page_url: (meta.prev_page_url as string | null) ?? null,
  };
}

export function resolveCustomerPaginationState(
  meta: PaginationMeta | undefined,
  itemCount: number,
  isFetching = false,
) {
  if (!meta || meta.total <= 0) return null;

  const currentPage = Math.max(1, meta.current_page || 1);
  const perPage = Math.max(1, meta.per_page || itemCount || 25);
  const lastPage = Math.max(1, meta.last_page || Math.ceil(meta.total / perPage));

  let from = meta.from;
  let to = meta.to;

  if (itemCount > 0 && (from <= 0 || to <= 0 || to < from)) {
    from = (currentPage - 1) * perPage + 1;
    to = Math.min(from + itemCount - 1, meta.total);
  }

  const hasMultiplePages = lastPage > 1;
  const canGoPrev = hasMultiplePages && currentPage > 1 && !isFetching;
  const canGoNext = hasMultiplePages && currentPage < lastPage && !isFetching;

  return {
    from,
    to,
    total: meta.total,
    currentPage,
    lastPage,
    hasMultiplePages,
    canGoPrev,
    canGoNext,
  };
}
